// CRM → Gestión Comercial → WhatsApp (/crm/whatsapp).
// La bandeja de conversaciones del canal de WhatsApp. RPCs del script 846.
//
// Dos cosas que la pantalla muestra ANTES de que el usuario escriba, porque
// descubrirlas después cuesta un mensaje rechazado o una multa:
//   · La ventana de 24 h. Fuera de ella WhatsApp sólo admite una plantilla
//     aprobada; el cuadro de texto se bloquea y lo explica.
//   · El consentimiento y las bajas. Si el titular pidió no ser contactado, no
//     hay forma de escribirle desde aquí.
//
// Mientras no exista el worker del canal, los mensajes salientes quedan en
// `pendiente`. La pantalla lo dice: no simula un envío que no ocurrió.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Ban, CheckCheck, Clock, Inbox, Loader2, Lock, MessageCircle,
  Search, Send, UserCheck, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fieldCls } from '@/features/inventario/shared';
import { LOCALE } from '@/lib/utils/regional';

const MSG = { headers: { 'Content-Profile': 'mensajeria', 'Accept-Profile': 'mensajeria' } };
const rpc = async <T,>(fn: string, args: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post(`/rpc/${fn}`, args, MSG)).data as T;

type Filtro = 'todas' | 'mias' | 'sin_asignar' | 'cerradas';

const FILTROS: { v: Filtro; l: string }[] = [
  { v: 'todas', l: 'Todas' },
  { v: 'mias', l: 'Mías' },
  { v: 'sin_asignar', l: 'Sin asignar' },
  { v: 'cerradas', l: 'Cerradas' },
];

interface ConvRow {
  id: string; numero_e164: string; titulo: string; partner_id: string | null;
  estado: string; no_leidos: number; ultimo_mensaje_at: string | null;
  vendedor_id: string | null; vendedor: string | null; mia: boolean;
  cuenta: string; simulada: boolean;
  ventana_minutos: number; ventana_abierta: boolean; bloqueo: string | null;
  ultimo: { texto: string; direccion: string; estado: string } | null;
}
interface Mensaje {
  id: string; fecha: string; direccion: 'entrante' | 'saliente'; tipo: string;
  texto: string | null; plantilla: string | null; estado: string;
  error: string | null; adjunto: string | null; usuario: string | null;
}
interface Detalle {
  id: string; numero_e164: string; titulo: string | null; estado: string;
  vendedor: string | null; mia: boolean; cuenta: string; simulada: boolean;
  ventana_abierta: boolean; ventana_minutos: number;
  envio: { bloqueo: string | null; texto_libre: boolean; aviso: string | null };
  mensajes: Mensaje[];
}
interface Veredicto { enviado: boolean; motivo?: string; mensaje?: string }

const hora = (s: string) =>
  new Date(s).toLocaleString(LOCALE, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

// «3 h 40 min» se lee de un vistazo; «220 minutos» hay que convertirlo.
function restante(min: number) {
  if (min <= 0) return 'cerrada';
  const h = Math.floor(min / 60);
  return h > 0 ? `${h} h ${min % 60} min` : `${min} min`;
}

function Ventana({ abierta, minutos }: { abierta: boolean; minutos: number }) {
  if (!abierta) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
        <Lock className="h-3 w-3" /> Ventana cerrada
      </span>
    );
  }
  // Menos de dos horas es el momento de contestar, no de dejarlo para mañana.
  const urge = minutos < 120;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
      urge ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
           : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}`}>
      <Clock className="h-3 w-3" /> {restante(minutos)}
    </span>
  );
}

export function BandejaWhatsApp() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [busqueda, setBusqueda] = useState('');
  const [sel, setSel] = useState<string | null>(null);

  const { data: convs = [], isFetching } = useQuery<ConvRow[]>({
    queryKey: ['msg-bandeja', filtro, busqueda],
    queryFn: () => rpc<ConvRow[]>('msg_bandeja', { _filtro: filtro, _busqueda: busqueda || null }),
    refetchInterval: 15000, staleTime: 0, refetchOnMount: 'always',
  });

  const sinLeer = convs.reduce((a, c) => a + Number(c.no_leidos || 0), 0);

  return (
    <div className="p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" /> WhatsApp
          </h1>
          <p className="text-sm text-muted-foreground">
            Conversaciones con clientes y prospectos
            {sinLeer > 0 && <> · <b>{sinLeer}</b> sin leer</>}
          </p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className={`${fieldCls} pl-8 w-64`} placeholder="Buscar cliente o número"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-1">
        {FILTROS.map((f) => (
          <button key={f.v} onClick={() => { setFiltro(f.v); setSel(null); }}
                  className={`rounded-full px-3 py-1 text-sm ${filtro === f.v
                    ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}>
            {f.l}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3" style={{ height: '34rem' }}>
          <div className="overflow-y-auto border-r">
            {isFetching && convs.length === 0 && (
              <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div>
            )}
            {!isFetching && convs.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Inbox className="mx-auto mb-2 h-6 w-6 opacity-50" />
                No hay conversaciones en este filtro
              </div>
            )}
            {convs.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)}
                      className={`w-full border-b px-3 py-2 text-left hover:bg-muted/50 ${sel === c.id ? 'bg-muted' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-medium">{c.titulo}</span>
                  {Number(c.no_leidos) > 0 && (
                    <span className="rounded-full bg-green-600 px-1.5 text-[10px] font-bold text-white">{c.no_leidos}</span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">{c.numero_e164}</div>
                {c.ultimo && (
                  <div className="truncate text-xs text-muted-foreground">
                    {c.ultimo.direccion === 'saliente' && '↩ '}{c.ultimo.texto}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {c.bloqueo
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        <Ban className="h-3 w-3" /> No contactar
                      </span>
                    : <Ventana abierta={c.ventana_abierta} minutos={c.ventana_minutos} />}
                  {c.vendedor
                    ? <span className="truncate text-[11px] text-muted-foreground">· {c.vendedor}</span>
                    : <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">· sin asignar</span>}
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-col md:col-span-2">
            {sel
              ? <Hilo id={sel} onCambio={() => qc.invalidateQueries({ queryKey: ['msg-bandeja'] })} />
              : <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  Elige una conversación para leer el hilo
                </div>}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Hilo({ id, onCambio }: { id: string; onCambio: () => void }) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [plantilla, setPlantilla] = useState('');
  const [simular, setSimular] = useState('');
  const fin = useRef<HTMLDivElement>(null);

  const key = ['msg-conv', id];
  const { data: d, isLoading } = useQuery<Detalle>({
    queryKey: key, queryFn: () => rpc<Detalle>('msg_conversacion', { _id: id }),
    refetchInterval: 15000, staleTime: 0,
  });

  const refrescar = () => { void qc.invalidateQueries({ queryKey: key }); onCambio(); };

  // Marcar leída al abrir: si no, el contador se queda en rojo para siempre.
  useEffect(() => {
    rpc('msg_leida', { _conversacion: id }).then(onCambio).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { fin.current?.scrollIntoView({ block: 'end' }); }, [d?.mensajes?.length]);

  const enviar = useMutation({
    mutationFn: () => rpc<Veredicto>('msg_enviar', {
      _conversacion: id,
      _texto: texto || null,
      _plantilla: plantilla || null,
    }),
    onSuccess: (v) => {
      // La RPC devuelve un veredicto: un rechazo NO es un error de red, es una
      // respuesta con su motivo — y con su constancia ya guardada.
      if (!v.enviado) { toast.error(v.mensaje ?? 'No se pudo enviar'); return; }
      setTexto(''); setPlantilla(''); refrescar();
      toast.success(v.mensaje ?? 'Mensaje encolado');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tomar = useMutation({
    mutationFn: () => rpc('msg_asignar', { _conversacion: id }),
    onSuccess: () => { refrescar(); toast.success('Conversación asignada a ti'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cerrar = useMutation({
    mutationFn: (reabrir: boolean) => rpc('msg_cerrar', { _conversacion: id, _reabrir: reabrir }),
    onSuccess: () => { refrescar(); toast.success('Estado actualizado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const entrante = useMutation({
    mutationFn: () => rpc('msg_simular_entrante', { _conversacion: id, _texto: simular }),
    onSuccess: () => { setSimular(''); refrescar(); toast.success('Mensaje entrante simulado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !d) {
    return <div className="flex flex-1 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  const puedeTexto = d.envio.texto_libre;
  const bloqueado = d.envio.bloqueo !== null;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{d.titulo ?? d.numero_e164}</div>
          <div className="truncate text-xs text-muted-foreground">
            {d.numero_e164} · {d.cuenta}{d.vendedor ? ` · atiende ${d.vendedor}` : ' · sin asignar'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!bloqueado && <Ventana abierta={d.ventana_abierta} minutos={d.ventana_minutos} />}
          {!d.mia && (
            <Button size="sm" variant="outline" onClick={() => tomar.mutate()} isLoading={tomar.isPending}>
              <UserCheck className="mr-1 h-4 w-4" /> Tomarla
            </Button>
          )}
          <Button size="sm" variant="ghost" title={d.estado === 'cerrada' ? 'Reabrir' : 'Cerrar'}
                  onClick={() => cerrar.mutate(d.estado === 'cerrada')}>
            {d.estado === 'cerrada' ? <Inbox className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto bg-muted/20 p-4">
        {d.mensajes.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Todavía no hay mensajes en esta conversación
          </p>
        )}
        {d.mensajes.map((m) => (
          <div key={m.id} className={`flex ${m.direccion === 'saliente' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
              m.direccion === 'saliente'
                ? 'bg-green-100 dark:bg-green-900/40'
                : 'bg-card border'}`}>
              {m.plantilla && (
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Plantilla · {m.plantilla}
                </div>
              )}
              <div className="whitespace-pre-wrap break-words">{m.texto ?? `[${m.tipo}]`}</div>
              <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                {hora(m.fecha)}
                {m.direccion === 'saliente' && (
                  m.estado === 'pendiente'
                    ? <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> pendiente</span>
                    : m.estado === 'fallido'
                      ? <span className="inline-flex items-center gap-0.5 text-red-600"><AlertTriangle className="h-3 w-3" /> falló</span>
                      : <span className="inline-flex items-center gap-0.5"><CheckCheck className="h-3 w-3" /> {m.estado}</span>
                )}
              </div>
              {m.error && <div className="mt-1 text-[11px] text-red-600">{m.error}</div>}
            </div>
          </div>
        ))}
        <div ref={fin} />
      </div>

      {d.envio.aviso && (
        <div className={`flex items-start gap-2 border-t px-4 py-2 text-xs ${
          bloqueado ? 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                    : 'bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100'}`}>
          {bloqueado ? <Ban className="mt-0.5 h-4 w-4 shrink-0" /> : <Clock className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{d.envio.aviso}</span>
        </div>
      )}

      <div className="border-t p-3">
        {bloqueado ? (
          <p className="text-center text-sm text-muted-foreground">
            No se puede escribir a este número.
          </p>
        ) : (
          <>
            {!puedeTexto && (
              <input className={`${fieldCls} mb-2`} value={plantilla}
                     onChange={(e) => setPlantilla(e.target.value)}
                     placeholder="Nombre de la plantilla aprobada (p. ej. seguimiento_cotizacion)" />
            )}
            <div className="flex items-end gap-2">
              <textarea
                className={`${fieldCls} min-h-[38px] flex-1 resize-y`} rows={1}
                value={texto} onChange={(e) => setTexto(e.target.value)}
                disabled={!puedeTexto}
                placeholder={puedeTexto
                  ? 'Escribe tu respuesta…'
                  : 'Fuera de la ventana de 24 h sólo se puede enviar una plantilla'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && puedeTexto && texto.trim()) {
                    e.preventDefault(); enviar.mutate();
                  }
                }} />
              <Button size="sm" isLoading={enviar.isPending}
                      disabled={puedeTexto ? !texto.trim() : !plantilla.trim()}
                      onClick={() => enviar.mutate()}>
                <Send className="mr-1 h-4 w-4" /> Enviar
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              El canal todavía no está conectado: el mensaje queda en cola con estado «pendiente».
            </p>
          </>
        )}

        {d.simulada && (
          <div className="mt-3 rounded border border-dashed p-2">
            <p className="mb-1 text-[11px] font-medium text-muted-foreground">
              Canal de simulación — escribe aquí lo que respondería el cliente para ver
              cómo se reabre la ventana de 24 h
            </p>
            <div className="flex items-center gap-2">
              <input className={`${fieldCls} flex-1`} value={simular}
                     onChange={(e) => setSimular(e.target.value)}
                     placeholder="Mensaje entrante del cliente…" />
              <Button size="sm" variant="outline" isLoading={entrante.isPending}
                      disabled={!simular.trim()} onClick={() => entrante.mutate()}>
                Simular
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
